<template>
  <div class="dashboard">
    <div class="main-content">
      <navbar-dashboard name="Integraciones"></navbar-dashboard>

      <div class="ew-view">
        <page-header title="Integraciones" subtitle="Credenciales de conexión con cada paquetería. Los valores sensibles se muestran ocultos.">
          <button class="btn btn-primary" @click="openModal()">
            <i class="bi bi-plus-lg mr-1"></i> Nueva integración
          </button>
        </page-header>

        <div v-if="loading" class="ew-integrations">
          <div v-for="n in 3" :key="'sk-' + n" class="ew-section">
            <span class="ew-skeleton mb-3" style="width: 140px; height: 1.2rem;"></span>
            <span class="ew-skeleton mb-2"></span>
            <span class="ew-skeleton" style="width: 60%;"></span>
          </div>
        </div>

        <div v-else-if="loadError" class="ew-section p-0">
          <empty-state error icon="bi bi-plug" title="No pudimos cargar las integraciones"
            text="Revisa tu conexión e inténtalo de nuevo.">
            <button class="btn btn-outline-secondary" @click="fetchConfigs">
              <i class="bi bi-arrow-clockwise mr-1"></i> Reintentar
            </button>
          </empty-state>
        </div>

        <div v-else-if="!configs.length" class="ew-section p-0">
          <empty-state icon="bi bi-plug" title="Sin integraciones configuradas"
            text="Agrega las credenciales de una paquetería para empezar a cotizar con ella.">
            <button class="btn btn-primary" @click="openModal()">
              <i class="bi bi-plus-lg mr-1"></i> Nueva integración
            </button>
          </empty-state>
        </div>

        <div v-else class="ew-integrations">
          <div v-for="config in configs" :key="config._id" class="ew-section ew-integration">
            <div class="ew-integration-head">
              <div class="ew-integration-logo">{{ (config.name || '?').charAt(0) }}</div>
              <div class="flex-fill" style="min-width: 0;">
                <div class="d-flex align-items-center flex-wrap" style="gap: 6px;">
                  <h2 class="ew-integration-name">{{ carrierLabel(config.name) }}</h2>
                  <span class="badge" :class="config.active ? 'badge-success' : 'badge-secondary'">
                    {{ config.active ? 'Activa' : 'Inactiva' }}
                  </span>
                  <span class="badge" :class="config.mode === 'production' ? 'badge-primary' : 'badge-warning'">
                    {{ config.mode === 'production' ? 'Producción' : 'Pruebas' }}
                  </span>
                </div>
                <div class="ew-integration-meta">Actualizada {{ formatDate(config.updatedAt) }}</div>
              </div>
              <div class="text-nowrap">
                <button class="btn btn-sm btn-outline-secondary ew-icon-btn mr-1" @click="openModal(config)"
                  :aria-label="'Editar ' + config.name" v-b-tooltip.hover.top="'Editar'">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger ew-icon-btn" @click="deleteConfig(config.name)"
                  :aria-label="'Eliminar ' + config.name" v-b-tooltip.hover.top="'Eliminar'">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>

            <dl class="ew-keys">
              <template v-for="(value, key) in currentKeys(config)">
                <dt :key="'k-' + key">{{ formatLabel(key) }}</dt>
                <dd :key="'v-' + key">
                  <code class="ew-key-value">{{ displayValue(config, key, value) }}</code>
                  <button v-if="isSecret(key) && value" type="button" class="ew-key-btn"
                    :aria-label="isRevealed(config, key) ? 'Ocultar' : 'Mostrar'"
                    v-b-tooltip.hover.top="isRevealed(config, key) ? 'Ocultar' : 'Mostrar'"
                    @click="toggleReveal(config, key)">
                    <i class="bi" :class="isRevealed(config, key) ? 'bi-eye-slash' : 'bi-eye'"></i>
                  </button>
                  <button v-if="value" type="button" class="ew-key-btn" aria-label="Copiar"
                    v-b-tooltip.hover.top="copiedKey === config._id + key ? 'Copiado' : 'Copiar'"
                    @click="copyValue(config, key, value)">
                    <i class="bi" :class="copiedKey === config._id + key ? 'bi-check2 text-success' : 'bi-clipboard'"></i>
                  </button>
                </dd>
              </template>
            </dl>
          </div>
        </div>

        <!-- Modal dinámico -->
        <b-modal ref="carrierModal" hide-footer :title="modalTitle">
          <form @submit.prevent="saveConfig">
            <div class="form-group">
              <label for="carrier-name">Paquetería</label>
              <select id="carrier-name" v-model="form.name" @change="updateKeysForCarrier" class="custom-select"
                :disabled="!!editingId">
                <option disabled value="">Selecciona una paquetería</option>
                <option v-for="c in carrierOptions" :key="c.value" :value="c.value">{{ c.label }}</option>
              </select>
            </div>

            <div class="form-group">
              <label for="carrier-mode">Entorno</label>
              <select id="carrier-mode" class="custom-select" v-model="form.mode">
                <option value="test">Pruebas</option>
                <option value="production">Producción</option>
              </select>
            </div>

            <div class="custom-control custom-switch mb-3">
              <input type="checkbox" class="custom-control-input" v-model="form.active" id="activeCheck">
              <label class="custom-control-label" for="activeCheck">Integración activa</label>
            </div>

            <div v-for="(value, key) in form.keys[form.mode]" :key="key" class="form-group">
              <label :for="'key-' + key">{{ formatLabel(key) }}</label>
              <div class="ew-secret-input">
                <input :id="'key-' + key" :type="isSecret(key) && !formReveal[key] ? 'password' : 'text'"
                  class="form-control" v-model="form.keys[form.mode][key]" autocomplete="off" spellcheck="false">
                <button v-if="isSecret(key)" type="button" class="ew-key-btn"
                  :aria-label="formReveal[key] ? 'Ocultar' : 'Mostrar'" @click="$set(formReveal, key, !formReveal[key])">
                  <i class="bi" :class="formReveal[key] ? 'bi-eye-slash' : 'bi-eye'"></i>
                </button>
              </div>
            </div>

            <div class="d-flex justify-content-end border-top pt-3">
              <button type="button" class="btn btn-outline-secondary mr-2" @click="hideModal">Cancelar</button>
              <button type="submit" class="btn btn-primary">Guardar integración</button>
            </div>
          </form>
        </b-modal>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ew-integrations {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
  gap: 16px;
}

.ew-integrations .ew-section {
  margin-bottom: 0;
}

.ew-integration-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.ew-integration-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #e8ebf5;
  color: #031d7d;
  font-weight: 700;
}

.ew-integration-name {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}

.ew-integration-meta {
  margin-top: 2px;
  color: #6b7185;
  font-size: 0.78rem;
}

.ew-keys {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 8px 16px;
  margin: 0;
  padding-top: 16px;
  border-top: 1px solid #eceef5;
  font-size: 0.82rem;
}

.ew-keys dt {
  color: #6b7185;
  font-weight: 500;
  line-height: 28px;
}

.ew-keys dd {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
  margin: 0;
}

.ew-key-value {
  flex: 1 1 auto;
  min-width: 0;
  padding: 4px 8px;
  overflow: hidden;
  border-radius: 6px;
  background: #f6f7fb;
  color: #2f3446;
  font-size: 0.78rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ew-key-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 6px !important;
  background: transparent;
  color: #6b7185;
}

.ew-key-btn:hover {
  background: #eceef5;
  color: #1c2030;
}

.ew-secret-input {
  position: relative;
}

.ew-secret-input .form-control {
  padding-right: 40px;
}

.ew-secret-input .ew-key-btn {
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
}

@media (max-width: 575.98px) {
  .ew-integrations {
    grid-template-columns: 1fr;
  }

  .ew-keys {
    grid-template-columns: 1fr;
    gap: 2px;
  }

  .ew-keys dd {
    margin-bottom: 8px;
  }
}
</style>


<script>
import Navbar from './Header/Navbar'
import axios from "axios";
import commons from '../commons';

export default {
  name: "CarrierConfigs",
  data() {
    return {
      configs: [],
      form: {
        name: "",
        active: false,
        mode: "test",
        keys: {
          test: {},
          production: {},
        },
      },
      editingId: null,
      loading: true,
      loadError: false,
      revealed: {},
      formReveal: {},
      copiedKey: '',
      carrierOptions: [
        { value: 'EPAK', label: 'EPAK' },
        { value: 'FEDEX', label: 'FedEx' },
        { value: 'FEDEX_TRACK', label: 'FedEx Tracking' },
        { value: 'TRESGUERRAS', label: 'Tres Guerras' },
        { value: 'UPS', label: 'UPS' },
        { value: 'ENTREGA', label: 'En-Trega' },
      ],
    };
  },
  computed: {
    modalTitle() {
      return this.editingId ? "Editar integración" : "Nueva integración";
    },
  },
  methods: {
    // Inicializa keys según el carrier seleccionado
    initKeysForCarrier(carrierName) {
      const carriers = {
        EPAK: {
          test: { token: "", url: "" },
          production: { token: "", url: "" },
        },
        FEDEX: {
          test: { url: "", client_id: "", client_secret: "", account: "" },
          production: { url: "", client_id: "", client_secret: "", account: "" },
        },
        FEDEX_TRACK: {
          test: { url: "", client_id: "", client_secret: "", account: "" },
          production: { url: "", client_id: "", client_secret: "", account: "" },
        },
        ENTREGA: {
          test: { url: "", usuario: "", contrasena: "" },
          production: { url: "", usuario: "", contrasena: "" },
        },
        TRESGUERRAS: {
          test: { url: "", Access_Usr: "", Access_Pass: "" },
          production: { url: "", Access_Usr: "", Access_Pass: "" },
        },
        UPS: {
          test: { url: "", Client_ID: "", Client_Secret: "", cuenta: "" },
          production: { url: "", Client_ID: "", Client_Secret: "", cuenta: "" },
        },
      };
      return carriers[carrierName.toUpperCase()] || { test: {}, production: {} };
    },

    // Actualiza keys cuando se selecciona un carrier en el select
    updateKeysForCarrier() {
      if (this.form.name) {
        this.form.keys = this.initKeysForCarrier(this.form.name);
      }
    },

    formatLabel(key) {
      return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    async fetchConfigs() {
      this.loading = true;
      this.loadError = false;
      try {
        const res = await axios.get(`${commons.apiURL}/api/v1/credential`);
        this.configs = res.data || [];
      } catch (error) {
        console.error(error);
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },

    carrierLabel(name) {
      const found = this.carrierOptions.find(c => c.value === String(name).toUpperCase());
      return found ? found.label : name;
    },

    currentKeys(config) {
      return (config.keys && config.keys[config.mode]) || {};
    },

    // Llaves que no deben verse en pantalla a menos que se pidan explícitamente
    isSecret(key) {
      return /token|secret|pass|contrasena|password|api_?key/i.test(key);
    },

    isRevealed(config, key) {
      return !!this.revealed[config._id + key];
    },

    toggleReveal(config, key) {
      this.$set(this.revealed, config._id + key, !this.isRevealed(config, key));
    },

    displayValue(config, key, value) {
      if (!value) return '—';
      if (!this.isSecret(key) || this.isRevealed(config, key)) return value;
      const text = String(value);
      return '•'.repeat(12) + (text.length > 8 ? text.slice(-4) : '');
    },

    async copyValue(config, key, value) {
      try {
        await navigator.clipboard.writeText(String(value));
        this.copiedKey = config._id + key;
        setTimeout(() => { if (this.copiedKey === config._id + key) this.copiedKey = ''; }, 1500);
      } catch (error) {
        console.error(error);
      }
    },

    openModal(config = null) {
      this.formReveal = {};
      if (config) {
        this.editingId = config.name;
        this.form = JSON.parse(JSON.stringify(config));
      } else {
        this.editingId = null;
        this.form = {
          name: "",
          active: false,
          mode: "test",
          keys: { test: {}, production: {} },
        };
      }
      this.$refs.carrierModal.show();
    },

    hideModal() {
      this.$refs.carrierModal.hide();
    },

    async saveConfig() {
      try {
        if (this.editingId) {
          await axios.put(`${commons.apiURL}/api/v1/credential/${this.editingId}`, this.form);
        } else {
          await axios.post(`${commons.apiURL}/api/v1/credential`, this.form);
        }
        this.hideModal();
        this.fetchConfigs();
      } catch (error) {
        console.error(error);
      }
    },

    async deleteConfig(id) {
      const ok = await this.$bvModal.msgBoxConfirm(`Se eliminarán las credenciales de ${this.carrierLabel(id)} y dejará de cotizarse con esta paquetería.`, {
        title: 'Eliminar integración',
        okTitle: 'Eliminar',
        okVariant: 'danger',
        cancelTitle: 'Cancelar',
        cancelVariant: 'outline-secondary',
        centered: true
      });
      if (ok) {
        await axios.delete(`${commons.apiURL}/api/v1/credential/${id}`);
        this.fetchConfigs();
      }
    },

    formatDate(date) {
      if (!date) return '—';
      return new Date(date).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
    },
  },
  mounted() {
    this.fetchConfigs();
  },
  components: {
    'navbar-dashboard': Navbar,
  },
};
</script>



// WEBPACK FOOTER //
// src/components/Integrations.vue